import { normalize } from '@livv/utils/helpers';
import { HitAttributeHighlightResult } from 'instantsearch.js';
import truncate from 'lodash/truncate';

const SEPARATORS_TO_REMOVE = /[_&-/\\#,+()$~%."`:*?!<>{}]/g;

/**
 * Common words to remove from the query,
 * must be in lower case.
 */
export const WORDS_TO_REMOVE = new Set([
    'a',
    'all',
    'an',
    'and',
    'are',
    'as',
    'at',
    'au',
    'aussi',
    'avec',
    'avoir',
    'be',
    'can',
    'car',
    'ce',
    'cette',
    'comme',
    'd',
    'dans',
    'de',
    'des',
    'do',
    'does',
    'down',
    'du',
    'elle',
    'elles',
    'en',
    'est',
    'et',
    'ete',
    'etre',
    'faire',
    'for',
    'from',
    'had',
    'have',
    'he',
    'her',
    'his',
    'i',
    'il',
    'ils',
    'in',
    'is',
    'it',
    'je',
    'l',
    'la',
    'le',
    'les',
    'leur',
    'leurs',
    'mais',
    'many',
    'ne',
    'not',
    'nous',
    'of',
    'on',
    'or',
    'ou',
    'out',
    'par',
    'pas',
    'plus',
    'pour',
    'que',
    'qui',
    'sa',
    'se',
    'ses',
    'she',
    'si',
    'son',
    'sont',
    'sur',
    'that',
    'the',
    'there',
    'they',
    'this',
    'to',
    'tout',
    'un',
    'une',
    'up',
    'vous',
    'was',
    'we',
    'were',
    'who',
    'with',
    'x',
    'y',
    'you',
]);

/**
 * Truncates a string at given length at a word boundary and adds dots … in the end
 * @param text the full string to truncate
 * @param length length to truncate after (in characters). String will be truncated after the next word boundary.
 */
// eslint-disable-next-line import/prefer-default-export
export const truncateText = (text: string, length: number): string => {
    const truncatedText = truncate(text, { length, omission: ' …', separator: /[\s.?!:]/ });

    return truncatedText.length < text.length ? truncatedText : text;
};

export const testContainsSubstring = (str: string, substr: string): boolean => {
    return new RegExp(substr, 'i').test(str);
};

export const testContainsExactSubstring = (str: string, substr: string): boolean => {
    return new RegExp(`\\b${substr}\\b`, 'i').test(str);
};

/**
 * Replace any of & - / \ # , + ( ) $ ~ % . ' " ’ ` : * ? < > { }
 * or multiple spaces (\s\s+) with a single space. E.g. " Non-violence " becomes "non violence".
 * Replace unicode french alphabet characters by ascii equivalents.
 * lowercase and trim spaces around.
 * Remove articles "l'" and "d'" adjacent to words.
 */
export const normalizeWithSeparators = (str: string): string =>
    normalize(str.replace(SEPARATORS_TO_REMOVE, ' '), 'lower').replace(/\b(l'|d')/g, '');

export const splitAndNormalizeSearchValue = (searchValue: string): string[] =>
    normalizeWithSeparators(searchValue)
        .split(' ')
        // remove ending 's' for plural/singular invariance
        .map((word) => word.replace(/s$/, ''));

/**
 * This function extracts a specified number of lines from a given text.
 * It calculates the number of characters that can fit in a line based on the container width, font size, and a character width factor.
 * It then slices the text to the calculated maximum number of characters.
 *
 * @param {string} text - The text from which lines are to be extracted.
 * @param {number} containerWidth - The width of the container in which the text is displayed.
 * @param {number} fontSize - The size of the font used to display the text.
 * @param {number} numLines - The number of lines to be extracted from the text.
 * @param {number} charWidthFactor - The factor used to calculate the approximate width of each character.
 *
 * @returns {string} - The extracted lines from the text.
 */
export const extractLines = (
    text: string,
    containerWidth: number,
    fontSize: number,
    numLines: number,
    charWidthFactor: number,
): string => {
    const charWidth = fontSize * charWidthFactor; // Use the provided factor
    const charsPerLine = Math.floor(containerWidth / charWidth);
    const maxChars = charsPerLine * numLines;

    return text.slice(0, maxChars);
};

export const QUERY_SPLIT_REGEX = /"([^"]+)"|\b\w+\b/g;

/**
 * Function removes diacritics (àééë...) from text while preserving the original text
 * length in terms of the number of Unicode code characters.
 * @param text input string
 * @returns string without diacritics
 */
export const removeDiacritics = (text: string): string =>
    text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const queryToNormalizedWords = (query: string): string[] => {
    // Remove punctuations, multiple spaces
    const words = removeDiacritics(query).toLowerCase().match(QUERY_SPLIT_REGEX);

    // For empty string or without words, match returns null, so return an empty array
    if (!words) {
        return [];
    }

    // Remove common words and quotes around exact match word groups
    const queryWords = words.reduce((acc: string[], word: string) => {
        if (word.startsWith('"') && word.endsWith('"')) {
            // Match method does not remove quotes around exact match word groups, so we need to remove them manually
            acc.push(word.slice(1, -1).replace(/\s+/g, ' ').trim());
        } else if (!WORDS_TO_REMOVE.has(word)) {
            acc.push(word);
        }

        return acc;
    }, []);

    return queryWords;
};

/**
 * Function add highlight tags in the original text
 * matchLevel is only used for Algolia search
 * @param text input string
 * @param words array of words to highlight
 * @param highlightTags start and end tags for highlighting
 * @returns {string, 'none' | 'partial'}  - matchLevel is for Alogia search
 */
export const addHighlight = (
    text: string,
    words: string[],
    highlightTags: { end: string; start: string },
): { updatedValue: string; matchLevel: HitAttributeHighlightResult['matchLevel'] } => {
    const TAGS_OFFSET = highlightTags.start.length + highlightTags.end.length;
    let matchLevel: HitAttributeHighlightResult['matchLevel'] = 'none';
    const regex = new RegExp(`\\b(${words.join('|')})\\b`, 'gi');

    // Normalize text but keep the original text length, which is important
    // to map the matches from normalized text to the original text.
    const normalizedText = removeDiacritics(text);

    let accumOffset = 0;

    let updatedValue = text;
    for (const match of normalizedText.matchAll(regex)) {
        if (match.index === undefined) {
            continue;
        }

        matchLevel = 'partial';

        const start = match.index + accumOffset;
        const end = start + match[0].length;
        updatedValue =
            updatedValue.slice(0, start) +
            highlightTags.start +
            updatedValue.slice(start, end) +
            highlightTags.end +
            updatedValue.slice(end);
        accumOffset += TAGS_OFFSET;
    }

    return { matchLevel, updatedValue };
};

export const LOREM_TEXT =
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.';

/**
 * Generate a Lorem Ipsum text with a specified number of lines.
 * @param lines The number of lines to generate.
 * @returns The generated Lorem Ipsum text.
 */
export const generateLoremIpsum = (lines: number): string | null => {
    if (lines <= 0) {
        return null;
    }

    return Array(lines).fill(LOREM_TEXT).join(' ');
};
